import ConfirmModal from "../confirm.modal";
import type { ModalComponentProps } from "../../../types/modal.types";
import * as React from "react";
import type { getUserForm } from "../../../lib/apis/dtos/request/user.request";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useUserByOwnerId, useUserList } from "../../../lib/apis/hooks/user.hook";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import Button from "../../ui/button";
import { useModalStore } from "../../../store/useModalStore";
import { MODAL_KEY } from "../../../lib/constants/modal.constants";
import axios from "axios";
import { useZoneGroup } from "../../../lib/apis/hooks/category.hook";
import { flushSync } from "react-dom";
import type { newStoreForm } from "../../../lib/apis/dtos/request/store.request";
import { createStore } from "../../../lib/apis/v1/store.service";
import FailSnackbar from "../../ui/snackbar/fail.component";
import SuccessSnackbar from "../../ui/snackbar/success.component";
import { getQueryClient } from "../../../config/queryClient.config";
import { convertRegionData } from "../../../utils/zone.utils";

interface ConvertedZone {
  zoneId: number;
  name: string;
}

export default function BranchCreateModal({ id, onClose }: ModalComponentProps) {
  const { openModal } = useModalStore();
  const userForm = { page: 0, size: 1000, role: "OWNER" } as getUserForm;

  const [form, setForm] = React.useState<newStoreForm>({
    storeName: "",
    storeLink: "",
    storeAddressDetail: "",
    storeAddress: "",
    userId: 0,
    ownerId: "",
    partnerIds: [] as string[],
    latitude: "",
    longitude: "",
    storeAreaName: "",
    ZoneId: "",
    zoneName: ""
  });
  const [, setSelectOwnerName] = React.useState<string>("");
  const [, setSelectPartnerName] = React.useState<string[]>([]);
  const [convertedZone, setConvertedZone] = React.useState<ConvertedZone[]>([]);

  const { data, isSuccess } = useQuery({
    ...useUserList(userForm),
    enabled: true,
  });
  const { result } = data || {};
  const content = result?.content || [];

  const { data: partnerData } = useQuery({
    ...useUserByOwnerId({ ownerId: form?.ownerId || "", page: 0, size: 1000 }),
    enabled: !!form?.ownerId,
  })

  const { result: partnerResult } = partnerData || {};
  const partnerContent = partnerResult?.content || [];

  const { data: zoneGroupData, isSuccess: isSuccessZoneGroup, refetch } = useQuery({
    ...useZoneGroup(),
    enabled: !!form.storeAddress,
  });

  const zoneContent = zoneGroupData?.result?.zoneGroups || [];


  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;

    if (id == "phone") {
      const numbersOnly = value.replace(/[^0-9]/g, "");
      setForm((state) => ({ ...state, [id]: numbersOnly }));
    } else {
      setForm((state) => ({ ...state, [id]: value }));
    }
  };

  React.useEffect(() => {
    if (isSuccessZoneGroup) {
      setConvertedZone(convertRegionData(zoneContent));
    }
  }, [isSuccessZoneGroup]);

  const handleAddressChange = () => {
    openModal(MODAL_KEY.Postcode, {
      onComplate: async (item) => {
        const { data, status } = await axios.get(`https://dapi.kakao.com/v2/local/search/address.json?query=${item.jibunAddress}`, {
          headers: {
            'Authorization': `KakaoAK 12c86c4b63a51e85705750f0e6de28e2`
          }
        });
        if (status !== 200) {
          FailSnackbar("좌표 조회 실패")
          return;
        }
        const { documents } = data as { documents: { x: string, y: string }[] }
        const storeAreaName = item.sigungu.split(" ");

        flushSync(() => {
          setForm((state) => ({
            ...state,
            storeAddress: item.address,
            latitude: documents[0].y,
            longitude: documents[0].x,
            storeAreaName: `${item.sido} ${storeAreaName[0]}`,
          }))
        })
        refetch()
      }
    })
  }

  const storeMutation = useMutation({
    mutationFn: (form: newStoreForm) => createStore(form),
    onSuccess: (res) => {
      const { error, message } = res;
      if (error) {
        FailSnackbar(message)
      } else {
        SuccessSnackbar("지점 생성 완료")
        getQueryClient().invalidateQueries({ queryKey: ["store-list"] })
        onClose?.();
      }
    }

  })

  const disabled = !form.storeName || !form.storeLink || !form.userId || !form.ZoneId;


  return (
    <ConfirmModal
      title="지점 생성"
      onClose={onClose}
      rightButton={{
        title: "지점 생성",
        variant: disabled ? "disabled" : "primary",
        disabled: disabled,
        onClick: () => {
          storeMutation.mutate(form);
        },
      }}
      id={id}
    >
      <p className="text-[16px] py-4 text-gray-60">입점 희망하는 지점의 정보를 입력해주세요.</p>

      <div className="flex flex-col gap-4 mt-4">
        <Autocomplete
          disablePortal
          options={content || []}
          loading={!isSuccess}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (!option) return "";
            return `${option.username}(${option.loginId})`;
          }}
          value={content?.find((item) => item.loginId?.toString() === form.ownerId) || null}

          slotProps={{
            popper: {
              sx: { zIndex: 3000 }
            },
          }}
          onChange={(_, newValue) => {
            if (typeof newValue === "string" || !newValue) {
              setForm((state) => ({ ...state, ownerId: "", userId: 0 }));
              setSelectOwnerName("");
              return;
            };
            setForm((state) => ({
              ...state,
              ownerId: newValue.loginId?.toString() || "",
              userId: newValue.userId || 0
            }));
            setSelectOwnerName(newValue.username || "");
          }}
          freeSolo

          renderInput={(params) => (
            <TextField
              {...params}
              label="오너 배정"
              placeholder="오너명으로 검색"
              InputLabelProps={{ shrink: true }} // 구형 버전 대응 (v5/v6 호환)
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {!isSuccess ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.loginId}>
              {option.username}({option.loginId})
            </li>
          )}
        />
        <Autocomplete
          multiple
          id="partner-autocomplete"
          className="w-[552px]"
          disabled={!form.ownerId}
          disableCloseOnSelect
          options={isSuccess ? partnerContent.filter((p) => p.role === "PARTNER") : []}
          getOptionLabel={(option) =>
            typeof option === 'string'
              ? option
              : `${option.username}(${option.loginId})`
          }

          value={isSuccess
            ? partnerContent.filter((p) => form.partnerIds.includes(String(p.userId)))
            : []
          }

          onChange={(_, newValue) => {
            const newIds = newValue.map((item) => String(item.userId));
            const newNames = newValue.map((item) => `${item.username}(${item.loginId})`);

            setForm((state) => ({ ...state, partnerIds: newIds }));
            setSelectPartnerName(newNames);
          }}

          isOptionEqualToValue={(option, value) => option.userId === value.userId}

          disablePortal
          slotProps={{
            popper: { sx: { zIndex: 9999 } }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="파트너 배정"
              placeholder={form.partnerIds.length === 0 ? "파트너명으로 검색" : ""}
              InputLabelProps={{
                ...params.InputLabelProps,
                shrink: true,
              }}
            />
          )}
        />

        <TextField
          id="storeName"
          className="w-[552px] h-[56px]"
          label="지점명*"
          placeholder="지점명 입력 (ex. 강남텔레콤 강남점)"
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={changeHandler}
        />
        <TextField
          id="storeLink"
          className="w-[552px] h-[56px]"
          label="연결 링크*"
          placeholder="연결 링크 입력 (ex.카톡 오픈채팅방/ 네이버 밴드 채팅방 등)"
          value={form.storeLink}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            changeHandler(e as React.ChangeEvent<HTMLInputElement>);
          }}
        />
        <div>
          <p className="text-style-compact-02 text-black/60 font-normal pb-2">지점 주소*</p>
          {form.storeAddress ? (
            <div className="flex-row items-center gap-2">
              <p className="text-style-compact-04 font-normal">{form.storeAddress} {`(${form.latitude}, ${form.longitude})`}</p>
              <span className="text-style-compact-04 text-primary-50 font-normal cursor-pointer gap-1"
                onClick={handleAddressChange}
              >주소 변경</span>
            </div>

          ) : (
            <Button
              variant={"outline"}
              label="주소 검색"
              onClick={handleAddressChange}
            />
          )}

        </div>
        {form.storeAddress && (
          <Autocomplete
            id="zone-select-autocomplete"
            className="w-[552px]"
            options={isSuccessZoneGroup ? convertedZone : []}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || "")}
            value={convertedZone?.find((z) => z.zoneId.toString() === form.ZoneId) || null}

            onChange={(_, newValue) => {
              if (newValue) {
                setForm((state) => ({
                  ...state,
                  ZoneId: newValue.zoneId.toString(),
                  zoneName: newValue.name
                }));
              } else {
                setForm((state) => ({ ...state, ZoneId: "", zoneName: "" }));
              }
            }}

            disablePortal
            slotProps={{
              popper: { sx: { zIndex: 9999 } }
            }}

            renderInput={(params) => (
              <TextField
                {...params}
                label="지역 선택*"
                placeholder="지역명으로 검색 (ex. 경기 성남시)"
                InputLabelProps={{
                  ...params.InputLabelProps,
                  shrink: true, // 라벨 상단 고정
                }}
              />
            )}
          />
        )}
        {form.ZoneId && (
          <TextField
            id="storeAddressDetail"
            className="w-[552px] h-[56px]"
            label="상세 주소 입력"
            placeholder="상세 주소 입력 (ex. 동, 호수)"
            inputMode="text"
            value={form.storeAddressDetail}
            inputProps={{ maxLength: 11 }}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={changeHandler}
          />
        )}
      </div>
    </ConfirmModal>
  );
}